import React, { memo, useEffect } from "react";
import { compose } from "redux";
import { connect } from "react-redux";
import { reduxForm } from "redux-form";
import { createStructuredSelector } from "reselect";
import get from "lodash/get";
import map from "lodash/map";
import {
  fetchPlan,
  fetchReferralCode,
  fetchAddressLookup,
  fetchDeliverySlots,
  resetDeliverySlots,
  clearData,
  setData,
  createSelfActivationDeliveryOrder,
  changeFormField
} from "ducks/actions";
import {
  makeSelectPageValidations,
  makeSelectIsFetchingData,
  makeSelectSelfActivationForm,
  makeSelectAddons,
  makeSelectPreSelectedAddons,
  makeSelectDefaultPlanAddons,
  makeSelectPayMonthlyItems,
  makeSelectPayTodayItems,
  makeSelectReferralCode,
  makeSelectDefaultPromoCodeOffers,
  // makeSelectPlanFAQ,
  makeSelectCountries,
  makeSelectStates,
  makeSelectOtherCountries,
  makeSelectPrimaryDocTypes,
  makeSelectMinimumAge,
  makeSelectDateOptions,
  makeSelectMonthOptions,
  makeSelectYearOptions,
  makeSelectFormattedSelectedNumber,
  makeSelectDeliveryMethod,
  makeSelectDeliveryMethods,
  makeSelectCombinedDeliveryDates,
  makeSelectDeliveryDates,
  makeSelectFreeDeliverySlots,
  makeSelectSpecialDeliverySlots,
  makeSelectPostalCodeOptions,
  makeSelectPersonalDetailsFormInitialValues,
  makeSelectHasAddressError,
  makeSelectEligiblePlans,
  makeSelectPlanDetails,
  makeSelectVerifyIdentityPayload,
  makeSelectSelfActivationOrderParams,
  makeSelectNumberType,
  makeSelectValidateIccId,
  makeSelectDetailsFromIccidVerification,
  makeSelectPreSelectedOrderAddons,
  makeSelectSelectedAddons,
  makeSelectMultiSimOrder,
  makeSelectSimCount,
  makeSelectSelectedNumberType,
  makeSelectShow5GAddon,
  makeSelectShowSectionListModal,
  makeSelectShowDeviceCompatibilityChecker,
  makeSelectShow5GDeviceCompatibilityChecker,
  makeSelectPreselected5GAddon,
  makeSelectIs5GAddonSelected,
  makeSelectIs5GCompatible,
  makeSelectEnableNextButtonWithDeviceChecker,
  makeSelectHideComponentsWithDeviceChecker,
  makeSelectSelectedMobileDeviceBrand,
  makeSelectFilteredMobileDeviceModels,
  makeSelectSelectedMobileDevice,
  makeSelectIs5GBundleAddonSelected,
  makeSelectEnableInternationalComms
} from "ducks/selectors";
import validate from "validations";
import SmartRender from "containers/SmartRender";
import { forms, paths, SELF_ACTIVATION_SIM } from "utils/constants";
import Spinner, { styles as spinnerStyles } from "components/Spinner";
import { urlParam, getChannelFromUrl, getURLParamValue } from "utils/helpers";

const SelfActivationDeliveryHOC = Component =>
  function Comp(props) {
    const {
      match,
      fetchPlan,
      isFetchingPlan,
      clearDeliverySlots,
      preSelectedOrderAddons,
      handleSelectAddon,
      validateReferralCode,
      orderDetails: { selected_referral_code, plan, order }
    } = props;
    const { channel } = get(match, "params", {});
    const plan_name = urlParam("plan_name");
    const planParams = {};
    if (channel && plan_name) {
      planParams["channel"] = channel;
      planParams["plan_name"] = plan_name;
    }

    useEffect(
      () => {
        if (getChannelFromUrl() !== SELF_ACTIVATION_SIM) {
          fetchPlan(planParams);
          clearDeliverySlots();
        }
        selected_referral_code &&
          validateReferralCode(
            selected_referral_code,
            get(plan, "name"),
            null,
            get(order, "order_ref")
          );
        map(preSelectedOrderAddons, addOn => {
          handleSelectAddon(addOn.id, true);
        });
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      []
    );

    return isFetchingPlan ? (
      <Spinner size={200} sx={spinnerStyles.fullPage} />
    ) : (
      <Component {...props} />
    );
  };

export const mapStateToProps = createStructuredSelector({
  formValidations: makeSelectPageValidations(forms.SELF_ACTIVATION_FORM),
  formValues: makeSelectSelfActivationForm(),
  addons: makeSelectSelectedAddons(),
  referralCode: makeSelectReferralCode(),
  preSelectedOrderAddons: makeSelectPreSelectedOrderAddons(),
  defaultAddons: makeSelectPreSelectedAddons(),
  defaultPlanAddons: makeSelectDefaultPlanAddons(),
  addOns: makeSelectAddons(),
  planDetails: makeSelectPlanDetails(),
  payMonthlyItems: makeSelectPayMonthlyItems(),
  payTodayItems: makeSelectPayTodayItems(),
  defaultPromoCodeOffers: makeSelectDefaultPromoCodeOffers(),
  // planFAQ: makeSelectPlanFAQ(),
  orderDetails: makeSelectValidateIccId(),
  issuingStates: makeSelectStates(),
  orderParams: makeSelectSelfActivationOrderParams(),
  eligiblePlans: makeSelectEligiblePlans(),
  issuingCountries: makeSelectCountries(),
  topCountries: makeSelectCountries(),
  otherCountries: makeSelectOtherCountries(),
  primaryDocTypes: makeSelectPrimaryDocTypes(),
  minimumAge: makeSelectMinimumAge(),
  monthOptions: makeSelectMonthOptions(),
  dateOptions: makeSelectDateOptions(),
  sim_count: makeSelectSimCount(),
  yearOptions: makeSelectYearOptions(),
  selectedNumber: makeSelectFormattedSelectedNumber(),
  deliveryMethods: makeSelectDeliveryMethods(),
  deliverySlotDates: makeSelectDeliveryDates(),
  combinedDeliverySlotDates: makeSelectCombinedDeliveryDates("self_act_del"),
  freeDeliverySlotTime: makeSelectFreeDeliverySlots("self_act_del"),
  specialDeliverySlotTime: makeSelectSpecialDeliverySlots("self_act_del"),
  postalCodeOptions: makeSelectPostalCodeOptions(),
  payload: makeSelectVerifyIdentityPayload(),
  isMultiSimOrder: makeSelectMultiSimOrder(),
  show5GAddon: makeSelectShow5GAddon(),
  showDeviceCompatibilityChecker: makeSelectShowDeviceCompatibilityChecker(),
  show5GDeviceCompatibilityChecker: makeSelectShow5GDeviceCompatibilityChecker(),
  preselected5GAddon: makeSelectPreselected5GAddon(),
  is5GAddonSelected: makeSelectIs5GAddonSelected(),
  is5GCompatible: makeSelectIs5GCompatible(),
  enableNextButtonWithDeviceChecker: makeSelectEnableNextButtonWithDeviceChecker(),
  hideComponentsWithDeviceChecker: makeSelectHideComponentsWithDeviceChecker(),
  is5GBundleAddon: makeSelectIs5GBundleAddonSelected(),
  selectedMobileDeviceBrand: makeSelectSelectedMobileDeviceBrand(),
  filteredMobileDeviceModels: makeSelectFilteredMobileDeviceModels(),
  selectedMobileDevice: makeSelectSelectedMobileDevice(),
  showSectionListModal: makeSelectShowSectionListModal(),
  deliveryAddressError: makeSelectHasAddressError(
    forms.SELF_ACTIVATION_FORM,
    "delivery"
  ),

  isFetchingPlan: makeSelectIsFetchingData("planService"),
  skipPostalCodeValidation: () => true,
  hidePromo: () => getURLParamValue("hpromo"),
  hideAddon: () => getURLParamValue("haddon"),
  hidePlan: () => getURLParamValue("hplan"),
  hidePayToday: () => getURLParamValue("hpaytoday"),
  initialValues: state => {
    return {
      ...makeSelectPersonalDetailsFormInitialValues()(state),
      delivery_method: {
        ...makeSelectDeliveryMethod()(state)
      },
      number_type: makeSelectNumberType()(state),
      ...makeSelectDetailsFromIccidVerification()(state)
    };
  },
  selectedNumberType: makeSelectSelectedNumberType(),
  displayInternationalComms: makeSelectEnableInternationalComms()
});

export const mapDispatchToProps = dispatch => {
  return {
    fetchPlan: params => dispatch(fetchPlan(params)),
    validateReferralCode: (code, planName, deviceSku = null, orderRef) =>
      dispatch(
        fetchReferralCode({
          referral_code: code,
          plan_name: planName,
          order_ref: orderRef
        })
      ),
    resetReferralCode: () =>
      dispatch(clearData(["referralCode", "selectedAddons"])),
    fetchAddressLookup: params => dispatch(fetchAddressLookup(params)),
    fetchDeliverySlots: params => {
      dispatch(fetchDeliverySlots(params));
    },
    changeField: (field, value) => {
      dispatch(
        changeFormField({
          form: forms.SELF_ACTIVATION_FORM,
          field: field,
          value: value
        })
      );
    },
    hideModal: () => {
      dispatch(
        setData({
          key: "showOrderInfoModal",
          data: { success: false, loading: false }
        })
      );
    },
    setSectionListModal: (value = false) => {
      dispatch(
        setData({
          key: "showSectionListModal",
          data: { enabled: value }
        })
      );
    },
    resetDeliverySlots: () => dispatch(resetDeliverySlots()),
    createSelfActivationDeliveryOrder: params => {
      dispatch(
        createSelfActivationDeliveryOrder(
          params,
          ({ order_ref, plan_name }) => {
            window.location.href = `${paths.SELF_ACTIVATION_DELIVERY_SUCCESS}?order_ref=${order_ref}&plan_name=${plan_name}`;
          }
        )
      );
    },
    onSubmit: (navigate, payload, change) => {
      navigate();
    },
    handleSelectAddon: (addonId, selected) =>
      dispatch(
        setData({ key: "selectedAddons", data: { [addonId]: selected } })
      ),
    clearDeliverySlots: () => dispatch(clearData("deliverySlots"))
  };
};

const mergeProps = (stateProps, dispatchProps, ownProps) => ({
  ...stateProps,
  ...dispatchProps,
  ...ownProps,
  portInOnlyMode: get(stateProps, "referralCode.port_in_only"),
  messageData: {
    referralCode: get(stateProps, "referralCode.value")
  }
});

const withConnect = connect(mapStateToProps, mapDispatchToProps, mergeProps);
export default compose(
  withConnect,
  reduxForm({
    form: forms.SELF_ACTIVATION_FORM,
    destroyOnUnmount: false,
    validate
  }),
  memo
)(SelfActivationDeliveryHOC(SmartRender));
